import type { Distance, Margins, Point, SpatialRectangle } from "@pdfking/math"
import { Abs, Max, Min } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { Math } from "@pdfking/quantity"
import { NullQ } from "@pdfking/structs"
import type { GrowAxis, GrowContentKind, GrowGroup, GrowLayout } from "@pdfking/templates"
import { GrowFlapAxis, GrowLayoutAxis, GrowLayoutCore, GrowStack } from "@pdfking/templates"
import { RecordMerge } from "@pdfking/tfunc"

import type { GrowJoint, GrowLeaf, GrowSize, ResolvedGrow, ResolvedGrowNode } from "#internal"

import { ExpandDimension } from "./ExpandDimension"
import { BoxCorners } from "./GrowLeafShape"

type Edge = keyof Margins<unknown>
type Axis = "x" | "y"
type Box = SpatialRectangle<Distance>
type Flap = GrowLayout & { enum: "flap" }
type Fold = GrowLayout & { enum: "fold" }

// A piece as it is placed, its padding still growing as the bleed beside it
// is folded in.
type Placed = {
  kind: GrowContentKind
  meta: GrowLeaf["meta"]
  box: Box
  corners: Point<Distance>[]
  flip: Axis | undefined
  padding: { top: Distance; right: Distance; bottom: Distance; left: Distance }
}

type Node =
  | { enum: "stack"; axis: Axis; compact: boolean; children: Node[] }
  | { enum: "leaf"; leaf: Placed }
  | { enum: "bleed" }

type Joint = Omit<GrowJoint, "before" | "after"> & { before: Node; after: Node }

// Every piece below `node`, in tree order.
const placed = (node: Node): Placed[] =>
  node.enum === "leaf" ? [node.leaf] : node.enum === "stack" ? node.children.flatMap(placed) : []

// What of `node` lies on the crease across `axis` at its start or its end:
// of a stack on the axis, the item at that end — a part behind it reaches
// the crease only when the part on it has no width —; the whole of any
// other, every item of it on the crease.
const against = (node: Node, axis: Axis, start: boolean): Node =>
  node.enum === "stack" && node.axis === axis && node.children.length
    ? against(node.children[start ? 0 : node.children.length - 1]!, axis, start)
    : node

// `amount` more bleed on `edge` of every piece along that edge of `node`.
const reach = (node: Node, edge: Edge, amount: Distance): void => {
  if (node.enum === "bleed") return
  if (node.enum === "leaf") {
    node.leaf.padding[edge] = (node.leaf.padding[edge] + amount) as Distance
    return
  }
  const across = edge === "left" || edge === "right" ? "x" : "y"
  if (node.axis !== across) {
    for (const child of node.children) reach(child, edge, amount)
    return
  }
  // Stacked on the edge's axis: x runs left to right, y top to bottom.
  const first = edge === "left" || edge === "top"
  const child = node.children[first ? 0 : node.children.length - 1]
  if (child) reach(child, edge, amount)
}

// `layout` with its stacks on `axis` listed the other way round, through
// and through, each flap staying after its host: a flap's parts from the
// edge out.
const mirrored = (layout: GrowLayout, axis: GrowAxis): GrowLayout => {
  if (layout.enum !== "along" && layout.enum !== "across" && layout.enum !== "pair") return layout
  const layouts = layout.layouts.map(item => mirrored(item, axis))
  if (GrowLayoutAxis(layout) !== axis) return { ...layout, layouts }
  const groups: GrowLayout[][] = []
  for (const item of layouts) {
    if (item.enum === "flap" && groups.length) groups[groups.length - 1]!.push(item)
    else groups.push([item])
  }
  return { ...layout, layouts: [...groups].reverse().flat() }
}

// A stack's start on an axis is where its first item goes: the left on x,
// the top on y.
const lo = (box: Box, axis: Axis) => (axis === "x" ? box.x : box.y)
const hi = (box: Box, axis: Axis) => (axis === "x" ? box.x + box.width : box.y + box.height)
const length = (box: Box, axis: Axis) => (axis === "x" ? box.width : box.height)
// The range on `axis` from `from` past the start for `size`
const range = (box: Box, axis: Axis, from: number, size: number): [number, number] =>
  axis === "x"
    ? [box.x + from, box.x + from + size]
    : [box.y + box.height - from - size, box.y + box.height - from]
const boxed = (x: [number, number], y: [number, number]): Box => ({
  x: x[0] as Distance,
  y: y[0] as Distance,
  width: (x[1] - x[0]) as Distance,
  height: (y[1] - y[0]) as Distance,
})

/**
 * The grow's tree over the page `size`, its lengths worked out and every
 * piece placed in the grow's frame, y up: `along` runs on x and `across`
 * on y, or the other way round when the page opens at the top. Stacks list
 * their items left to right and from the top down. A run of bleed between two
 * pieces is shared half each as padding, a run that ends at the grow's edge
 * goes whole to the piece beside it; the pieces tile the grow exactly. A fold
 * takes no room: it is the crease halfway between the layouts either side. A
 * flap is placed out of its host's edge, its parts from the edge out, its
 * crease the edge; the grow is as big as everything placed, from its lower
 * left.
 */
export const ResolveGrow = (
  layout: GrowLayout,
  size: GrowSize,
  opening_top: boolean
): ResolvedGrow => {
  const axis_of = (axis: GrowAxis): Axis => ((axis === "along") === !opening_top ? "x" : "y")
  const grow_axis = (axis: Axis): GrowAxis => (axis_of("along") === axis ? "along" : "across")
  // What `node` takes in its stack on `axis`, its flaps hanging out of it
  const extent = (node: GrowLayout, axis: Axis, parent: GrowAxis): Distance | undefined => {
    const expr = GrowLayoutCore(node, grow_axis(axis), parent)
    return NullQ(expr) ? undefined : ExpandDimension(expr, size)
  }
  const expand = (expr: Math) => ExpandDimension(expr, size)

  const leaves: Placed[] = []
  const joints: Joint[] = []

  // `flap` placed out of `edge` of its host in `box`, creased on it
  const hang = (flap: Flap, host: Node, box: Box): void => {
    const out = axis_of(GrowFlapAxis(flap))
    const along: Axis = out === "x" ? "y" : "x"
    const first = flap.edge === "left" || flap.edge === "top"
    const depth = extent(flap.layout, out, grow_axis(out))
    if (NullQ(depth)) throw new Error("grow flap names no depth")
    const from = expand(flap.from)
    const run = NullQ(flap.length)
      ? (extent(flap.layout, along, grow_axis(out)) ?? length(box, along) - from)
      : expand(flap.length)
    const span = range(box, along, from, run)
    const reach: [number, number] = first
      ? out === "x"
        ? [box.x - depth, box.x]
        : [box.y + box.height, box.y + box.height + depth]
      : out === "x"
        ? [box.x + box.width, box.x + box.width + depth]
        : [box.y - depth, box.y]
    const slot = out === "x" ? boxed(reach, span) : boxed(span, reach)
    const child = place(
      first ? mirrored(flap.layout, grow_axis(out)) : flap.layout,
      slot.x,
      slot.y,
      slot.width,
      slot.height
    )
    // The tip, the far edge, drawn in by the taper at either end: the
    // start's first, the end's then
    const tip = first === (out === "x") ? lo(slot, out) : hi(slot, out)
    const taper = flap.taper.map(expand)
    const at = (corner: Point<Distance>) => (out === "x" ? corner.x : corner.y)
    const on = (corner: Point<Distance>) => (along === "x" ? corner.x : corner.y)
    const sign = along === "x" ? 1 : -1
    const moved = (corner: Point<Distance>, by: number): Point<Distance> =>
      along === "x"
        ? { ...corner, x: (corner.x + by) as Distance }
        : { ...corner, y: (corner.y + by) as Distance }
    for (const leaf of placed(child)) {
      const edge = leaf.corners.filter(corner => Abs(at(corner) - tip) < 1e-9)
      if (edge.length !== 2) continue
      const [a, b] = [...edge].sort((p, q) => sign * (on(p) - on(q)))
      leaf.corners = leaf.corners.map(corner =>
        corner === a
          ? moved(corner, sign * taper[0]!)
          : corner === b
            ? moved(corner, -sign * taper[1]!)
            : corner
      )
    }
    joints.push({
      axis: out,
      at: (first
        ? out === "x"
          ? lo(box, out)
          : hi(box, out)
        : out === "x"
          ? hi(box, out)
          : lo(box, out)) as Distance,
      span: [span[0] as Distance, span[1] as Distance],
      before: against(first ? child : host, out, false),
      after: against(first ? host : child, out, true),
      angle: flap.angle,
      step: flap.step,
      ajar: flap.ajar,
    })
  }

  // `node` placed with its lower left corner at `x`, `y`, given `width` x
  // `height` when it names no size of its own on an axis.
  const place = (
    node: GrowLayout,
    x: Distance,
    y: Distance,
    width: Distance,
    height: Distance
  ): Node => {
    if (node.enum === "bleed" || node.enum === "fold" || node.enum === "flap")
      return { enum: "bleed" }
    if (node.enum === "content") {
      const box = { x, y, width, height }
      const leaf: Placed = {
        kind: node.kind,
        meta: node.meta,
        box,
        corners: BoxCorners(box),
        flip: NullQ(node.meta.flip) ? undefined : axis_of(node.meta.flip),
        padding: {
          top: ZERO_DISTANCE,
          right: ZERO_DISTANCE,
          bottom: ZERO_DISTANCE,
          left: ZERO_DISTANCE,
        },
      }
      leaves.push(leaf)
      return { enum: "leaf", leaf }
    }
    const own = GrowLayoutAxis(node)
    const axis = axis_of(own)
    // Each group's child, a bleed's as nothing
    const groups: (GrowGroup & { child: Node | undefined })[] = GrowStack(node.layouts).map(
      group => ({ ...group, child: undefined })
    )
    // Along x from the left, along y from the top.
    let at = axis === "x" ? x : ((y + height) as Distance)
    // The layout before, in its slot, and the fold since, past any bleed
    let last: { node: Node; box: Box } | undefined
    let carried: Fold | undefined
    for (const group of groups) {
      const item = group.item
      const crease = group.fold ?? carried
      const w = extent(item, "x", own) ?? width
      const h = extent(item, "y", own) ?? height
      let child: Node
      let slot: Box
      if (axis === "x") {
        slot = { x: at, y, width: w, height: h }
        child = place(item, at, y, w, h)
        at = (at + w) as Distance
      } else {
        at = (at - h) as Distance
        slot = { x, y: at, width: w, height: h }
        child = place(item, x, at, w, h)
      }
      if (child.enum === "bleed") carried = crease
      else {
        group.child = child
        if (crease && last)
          joints.push({
            axis,
            // Halfway from the one's end to the other's start: left to right
            // on x, from the top down on y
            at: ((axis === "x"
              ? hi(last.box, axis) + lo(slot, axis)
              : lo(last.box, axis) + hi(slot, axis)) / 2) as Distance,
            span: axis === "x" ? [y, (y + height) as Distance] : [x, (x + width) as Distance],
            before: against(last.node, axis, false),
            after: against(child, axis, true),
            angle: crease.angle,
            step: crease.step,
            ajar: crease.ajar,
          })
        last = { node: child, box: slot }
        carried = undefined
      }
      for (const flap of group.flaps) if (last) hang(flap, last.node, last.box)
    }
    // The bleed folded into its neighbours: a run between two is half each,
    // a run at an end goes whole to the one beside it.
    const edges: [Edge, Edge] = axis === "x" ? ["right", "left"] : ["bottom", "top"]
    let run = ZERO_DISTANCE
    let before: Node | undefined
    for (let i = 0; i <= groups.length; i++) {
      const group = groups[i]
      if (group && group.item.enum === "bleed") {
        run = (run + (extent(group.item, axis, own) ?? ZERO_DISTANCE)) as Distance
        continue
      }
      const child = group?.child
      if (run) {
        if (before && child) {
          reach(before, edges[0], (run / 2) as Distance)
          reach(child, edges[1], (run / 2) as Distance)
        } else if (before) reach(before, edges[0], run)
        else if (child) reach(child, edges[1], run)
        run = ZERO_DISTANCE
      }
      before = child
    }
    return {
      enum: "stack",
      axis,
      compact: node.enum === "pair",
      children: groups.flatMap(group => (group.child ? [group.child] : [])),
    }
  }

  const own = GrowLayoutAxis(layout)
  const core_width = extent(layout, "x", own)
  const core_height = extent(layout, "y", own)
  if (NullQ(core_width) || NullQ(core_height)) throw new Error("grow layout names no size")
  const root = place(layout, ZERO_DISTANCE, ZERO_DISTANCE, core_width, core_height)

  // Everything placed, its bleed with it, from the grow's lower left: the
  // flaps hanging past the root's are moved in with it
  const shift = {
    x: Min(...leaves.map(leaf => leaf.box.x - leaf.padding.left)),
    y: Min(...leaves.map(leaf => leaf.box.y - leaf.padding.bottom)),
  }
  const shifted = (point: Point<Distance>): Point<Distance> => ({
    x: (point.x - shift.x) as Distance,
    y: (point.y - shift.y) as Distance,
  })
  for (const leaf of leaves) {
    leaf.box = RecordMerge(leaf.box, shifted(leaf.box))
    leaf.corners = leaf.corners.map(shifted)
  }
  const width = Max(...leaves.map(leaf => hi(leaf.box, "x") + leaf.padding.right)) as Distance
  const height = Max(...leaves.map(leaf => hi(leaf.box, "y") + leaf.padding.top)) as Distance

  // Each piece's place among its kind, in tree order.
  const counts = new Map<GrowContentKind, number>()
  for (const leaf of leaves) counts.set(leaf.kind, (counts.get(leaf.kind) ?? 0) + 1)
  const seen = new Map<GrowContentKind, number>()
  const done = new Map<Placed, GrowLeaf>(
    leaves.map(leaf => {
      const index = (seen.get(leaf.kind) ?? 0) + 1
      seen.set(leaf.kind, index)
      return [leaf, { ...leaf, index, count: counts.get(leaf.kind)! }]
    })
  )
  const freeze = (node: Node): ResolvedGrowNode =>
    node.enum === "leaf"
      ? { enum: "leaf", leaf: done.get(node.leaf)! }
      : node.enum === "stack"
        ? {
            enum: "stack",
            axis: node.axis,
            compact: node.compact,
            children: node.children.map(freeze),
          }
        : { enum: "stack", axis: "x", compact: false, children: [] }

  const index = new Map(leaves.map((leaf, i) => [leaf, i]))
  const indices = (node: Node) => placed(node).map(leaf => index.get(leaf)!)
  return {
    size: { width, height },
    root: freeze(root),
    leaves: leaves.map(leaf => done.get(leaf)!),
    joints: joints.map(joint => {
      const other = joint.axis === "x" ? shift.y : shift.x
      return {
        ...joint,
        at: (joint.at - (joint.axis === "x" ? shift.x : shift.y)) as Distance,
        span: [(joint.span[0] - other) as Distance, (joint.span[1] - other) as Distance],
        before: indices(joint.before),
        after: indices(joint.after),
      }
    }),
  }
}
